// @flow
import * as Immutable from 'immutable';
import ImmutableModel from 'common/models/flow/ImmutableModel';

import type { WorkflowTypeEnumType } from 'common/enums/WorkflowTypeEnum';

import type { LocaleSettingsModelType } from 'common/models/flow/LocaleSettings';
import { LocaleSettings } from 'common/models/flow/LocaleSettings';

import type { SectionVisibilitySettingsModelType } from 'common/models/flow/SectionVisibilitySettings';
import { SectionVisibilitySettings } from 'common/models/flow/SectionVisibilitySettings';

import type { UnitSettingsModelType } from 'common/models/flow/UnitSettings';
import { UnitSettings } from 'common/models/flow/UnitSettings';

export type PlatformSettingsModelType = {
    analyticsDashboardPreferences: AnalyticsDashboardPreferencesModelType,
    platformSettings: NestedPlatformSettingsModelType,
    authSettings: AuthSettingsModelType,
    alertsSettings: AlertsSettingsModelType,
    upsellSettings: UpsellSettingsModelType,
    localeSettings: LocaleSettingsModelType,
    sectionVisibilitySettings: SectionVisibilitySettingsModelType,
    emailDigestSettings: ?EmailDigestSettingsModelType,
    analyticsSettings: AnalyticsSettingsModelType,
    unitSettings: ?UnitSettingsModelType
};

export type AnalyticsSettingsModelType = {
    enableLocationHeatMap: boolean,
    isCentralizedFilterEnabled: boolean,
    isOneYearDateRangeEnabled: boolean
};

export type EmailDigestSettingsModelType = {
    isEnabled: boolean,
    subscriptionLimit: number
};

export type AnalyticsDashboardPreferencesModelType = {
    workflowType: WorkflowTypeEnumType
};

export type NestedPlatformSettingsModelType = {
    whiteLabelSettings: ?WhiteLabelSettingsModelType,
    auditTrailSettings: ?AuditTrailSettingsModelType
};

export type AuditTrailSettingsModelType = {
    enabled: boolean
};

export type WhiteLabelSettingsModelType = {
    provider?: string
};

export type AuthSettingsModelType = {
    passwordAuthSettings: PasswordAuthSettingModelType,
    idleTimeoutSettings: IdleTimeoutSettingsModelType,
    qrEnabled: boolean
};

export type PasswordAuthSettingModelType = {
    isBruteForceProtectionEnabled: boolean
};

export type IdleTimeoutSettingsModelType = {
    // enable/disable timeout
    enabled: boolean,
    // time in seconds
    timeoutSeconds: number
};

export type AlertsSettingsModelType = {
    isAlertsEnabled: boolean
};

export type UpsellSettingsModelType = {
    isEnabled: boolean
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PlatformSettings extends ImmutableModel {
    static fromJS(json: PlatformSettingsModelType): PlatformSettings {
        const state: Object = Object.assign({}, json);
        state.analyticsDashboardPreferences = AnalyticsDashboardPreferences.fromJS(
            state.analyticsDashboardPreferences
        );
        state.platformSettings = NestedPlatformSettings.fromJS(
            state.platformSettings
        );
        state.authSettings = AuthSettings.fromJS(state.authSettings);
        state.alertsSettings = AlertsSettings.fromJS(state.alertsSettings);
        state.upsellSettings = UpsellSettings.fromJS(state.upsellSettings);
        state.localeSettings = LocaleSettings.fromJS(state.localeSettings);
        state.sectionVisibilitySettings = SectionVisibilitySettings.fromJS(
            state.sectionVisibilitySettings
        );
        state.emailDigestSettings = state.emailDigestSettings
            ? EmailDigestSettings.fromJS(state.emailDigestSettings)
            : state.emailDigestSettings;
        state.analyticsSettings = AnalyticsSettings.fromJS(
            state.analyticsSettings
        );
        state.unitSettings = state.unitSettings
            ? UnitSettings.fromJS(state.unitSettings)
            : state.unitSettings;
        return new this(Immutable.Map(state));
    }

    toJS(): PlatformSettingsModelType {
        return {
            analyticsDashboardPreferences: this.analyticsDashboardPreferences.toJS(),
            platformSettings: this.platformSettings.toJS(),
            authSettings: this.authSettings.toJS(),
            alertsSettings: this.alertsSettings.toJS(),
            upsellSettings: this.upsellSettings.toJS(),
            localeSettings: this.localeSettings.toJS(),
            sectionVisibilitySettings: this.sectionVisibilitySettings.toJS(),
            emailDigestSettings: this.emailDigestSettings
                ? this.emailDigestSettings.toJS()
                : this.emailDigestSettings,
            analyticsSettings: this.analyticsSettings.toJS(),
            unitSettings: this.unitSettings
                ? this.unitSettings.toJS()
                : this.unitSettings
        };
    }

    get analyticsDashboardPreferences(): AnalyticsDashboardPreferences {
        return this._state.get('analyticsDashboardPreferences');
    }

    setAnalyticsDashboardPreferences(
        analyticsDashboardPreferences: AnalyticsDashboardPreferences
    ): this {
        return this.clone(
            this._state.set(
                'analyticsDashboardPreferences',
                analyticsDashboardPreferences
            )
        );
    }

    get platformSettings(): NestedPlatformSettings {
        return this._state.get('platformSettings');
    }

    setPlatformSettings(platformSettings: NestedPlatformSettings): this {
        return this.clone(
            this._state.set('platformSettings', platformSettings)
        );
    }

    get authSettings(): AuthSettings {
        return this._state.get('authSettings');
    }

    setAuthSettings(authSettings: AuthSettings): this {
        return this.clone(this._state.set('authSettings', authSettings));
    }

    get alertsSettings(): AlertsSettings {
        return this._state.get('alertsSettings');
    }

    setAlertsSettings(alertsSettings: AlertsSettings): this {
        return this.clone(this._state.set('alertsSettings', alertsSettings));
    }

    get upsellSettings(): UpsellSettings {
        return this._state.get('upsellSettings');
    }

    setUpsellSettings(upsellSettings: UpsellSettings): this {
        return this.clone(this._state.set('upsellSettings', upsellSettings));
    }

    get localeSettings(): LocaleSettings {
        return this._state.get('localeSettings');
    }

    setLocaleSettings(localeSettings: LocaleSettings): this {
        return this.clone(this._state.set('localeSettings', localeSettings));
    }

    get sectionVisibilitySettings(): SectionVisibilitySettings {
        return this._state.get('sectionVisibilitySettings');
    }

    setSectionVisibilitySettings(
        sectionVisibilitySettings: SectionVisibilitySettings
    ): this {
        return this.clone(
            this._state.set(
                'sectionVisibilitySettings',
                sectionVisibilitySettings
            )
        );
    }

    get emailDigestSettings(): ?EmailDigestSettings {
        return this._state.get('emailDigestSettings');
    }

    setEmailDigestSettings(emailDigestSettings: ?EmailDigestSettings): this {
        return this.clone(
            this._state.set('emailDigestSettings', emailDigestSettings)
        );
    }

    get analyticsSettings(): AnalyticsSettings {
        return this._state.get('analyticsSettings');
    }

    setAnalyticsSettings(analyticsSettings: AnalyticsSettings): this {
        return this.clone(
            this._state.set('analyticsSettings', analyticsSettings)
        );
    }

    get unitSettings(): ?UnitSettings {
        return this._state.get('unitSettings');
    }

    setUnitSettings(unitSettings: ?UnitSettings): this {
        return this.clone(this._state.set('unitSettings', unitSettings));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AnalyticsDashboardPreferences extends ImmutableModel {
    static fromJS(
        json: AnalyticsDashboardPreferencesModelType
    ): AnalyticsDashboardPreferences {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AnalyticsDashboardPreferencesModelType {
        return {
            workflowType: this.workflowType
        };
    }

    get workflowType(): WorkflowTypeEnumType {
        return this._state.get('workflowType');
    }

    setWorkflowType(workflowType: WorkflowTypeEnumType): this {
        return this.clone(this._state.set('workflowType', workflowType));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class NestedPlatformSettings extends ImmutableModel {
    static fromJS(
        json: NestedPlatformSettingsModelType
    ): NestedPlatformSettings {
        const state: Object = Object.assign({}, json);
        state.whiteLabelSettings = state.whiteLabelSettings
            ? WhiteLabelSettings.fromJS(state.whiteLabelSettings)
            : state.whiteLabelSettings;
        state.auditTrailSettings = state.auditTrailSettings
            ? AuditTrailSettings.fromJS(state.auditTrailSettings)
            : state.auditTrailSettings;
        return new this(Immutable.Map(state));
    }

    toJS(): NestedPlatformSettingsModelType {
        return {
            whiteLabelSettings: this.whiteLabelSettings
                ? this.whiteLabelSettings.toJS()
                : this.whiteLabelSettings,
            auditTrailSettings: this.auditTrailSettings
                ? this.auditTrailSettings.toJS()
                : this.auditTrailSettings
        };
    }

    get whiteLabelSettings(): ?WhiteLabelSettings {
        return this._state.get('whiteLabelSettings');
    }

    setWhiteLabelSettings(whiteLabelSettings: ?WhiteLabelSettings): this {
        return this.clone(
            this._state.set('whiteLabelSettings', whiteLabelSettings)
        );
    }

    get auditTrailSettings(): ?AuditTrailSettings {
        return this._state.get('auditTrailSettings');
    }

    setAuditTrailSettings(auditTrailSettings: ?AuditTrailSettings): this {
        return this.clone(
            this._state.set('auditTrailSettings', auditTrailSettings)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AuditTrailSettings extends ImmutableModel {
    static fromJS(json: AuditTrailSettingsModelType): AuditTrailSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AuditTrailSettingsModelType {
        return {
            enabled: this.enabled
        };
    }

    get enabled(): boolean {
        return this._state.get('enabled');
    }

    setEnabled(enabled: boolean): this {
        return this.clone(this._state.set('enabled', enabled));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class WhiteLabelSettings extends ImmutableModel {
    static fromJS(json: WhiteLabelSettingsModelType): WhiteLabelSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): WhiteLabelSettingsModelType {
        const js: WhiteLabelSettingsModelType = {};

        if (this.provider != null) {
            js.provider = this.provider;
        }

        return js;
    }

    get provider(): ?string {
        return this._state.get('provider');
    }

    setProvider(provider: ?string): this {
        return this.clone(this._state.set('provider', provider));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AuthSettings extends ImmutableModel {
    static fromJS(json: AuthSettingsModelType): AuthSettings {
        const state: Object = Object.assign({}, json);
        state.passwordAuthSettings = PasswordAuthSetting.fromJS(
            state.passwordAuthSettings
        );
        state.idleTimeoutSettings = IdleTimeoutSettings.fromJS(
            state.idleTimeoutSettings
        );
        return new this(Immutable.Map(state));
    }

    toJS(): AuthSettingsModelType {
        return {
            passwordAuthSettings: this.passwordAuthSettings.toJS(),
            idleTimeoutSettings: this.idleTimeoutSettings.toJS(),
            qrEnabled: this.qrEnabled
        };
    }

    get passwordAuthSettings(): PasswordAuthSetting {
        return this._state.get('passwordAuthSettings');
    }

    setPasswordAuthSettings(passwordAuthSettings: PasswordAuthSetting): this {
        return this.clone(
            this._state.set('passwordAuthSettings', passwordAuthSettings)
        );
    }

    get idleTimeoutSettings(): IdleTimeoutSettings {
        return this._state.get('idleTimeoutSettings');
    }

    setIdleTimeoutSettings(idleTimeoutSettings: IdleTimeoutSettings): this {
        return this.clone(
            this._state.set('idleTimeoutSettings', idleTimeoutSettings)
        );
    }

    get qrEnabled(): boolean {
        return this._state.get('qrEnabled');
    }

    setQrEnabled(qrEnabled: boolean): this {
        return this.clone(this._state.set('qrEnabled', qrEnabled));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PasswordAuthSetting extends ImmutableModel {
    static fromJS(json: PasswordAuthSettingModelType): PasswordAuthSetting {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): PasswordAuthSettingModelType {
        return {
            isBruteForceProtectionEnabled: this.isBruteForceProtectionEnabled
        };
    }

    get isBruteForceProtectionEnabled(): boolean {
        return this._state.get('isBruteForceProtectionEnabled');
    }

    setIsBruteForceProtectionEnabled(
        isBruteForceProtectionEnabled: boolean
    ): this {
        return this.clone(
            this._state.set(
                'isBruteForceProtectionEnabled',
                isBruteForceProtectionEnabled
            )
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class IdleTimeoutSettings extends ImmutableModel {
    static fromJS(json: IdleTimeoutSettingsModelType): IdleTimeoutSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): IdleTimeoutSettingsModelType {
        return {
            enabled: this.enabled,
            timeoutSeconds: this.timeoutSeconds
        };
    }

    get enabled(): boolean {
        return this._state.get('enabled');
    }

    setEnabled(enabled: boolean): this {
        return this.clone(this._state.set('enabled', enabled));
    }

    get timeoutSeconds(): number {
        return this._state.get('timeoutSeconds');
    }

    setTimeoutSeconds(timeoutSeconds: number): this {
        return this.clone(this._state.set('timeoutSeconds', timeoutSeconds));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UpsellSettings extends ImmutableModel {
    static fromJS(json: UpsellSettingsModelType): UpsellSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): UpsellSettingsModelType {
        return {
            isEnabled: this.isEnabled
        };
    }

    get isEnabled(): boolean {
        return this._state.get('isEnabled');
    }

    setIsEnabled(isEnabled: boolean): this {
        return this.clone(this._state.set('isEnabled', isEnabled));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AlertsSettings extends ImmutableModel {
    static fromJS(json: AlertsSettingsModelType): AlertsSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AlertsSettingsModelType {
        return {
            isAlertsEnabled: this.isAlertsEnabled
        };
    }

    get isAlertsEnabled(): boolean {
        return this._state.get('isAlertsEnabled');
    }

    setIsAlertsEnabled(isAlertsEnabled: boolean): this {
        return this.clone(this._state.set('isAlertsEnabled', isAlertsEnabled));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AnalyticsSettings extends ImmutableModel {
    static fromJS(json: AnalyticsSettingsModelType): AnalyticsSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AnalyticsSettingsModelType {
        return {
            enableLocationHeatMap: this.enableLocationHeatMap,
            isCentralizedFilterEnabled: this.isCentralizedFilterEnabled,
            isOneYearDateRangeEnabled: this.isOneYearDateRangeEnabled
        };
    }

    get enableLocationHeatMap(): boolean {
        return this._state.get('enableLocationHeatMap');
    }

    setEnableLocationHeatMap(enableLocationHeatMap: boolean): this {
        return this.clone(
            this._state.set('enableLocationHeatMap', enableLocationHeatMap)
        );
    }

    get isCentralizedFilterEnabled(): boolean {
        return this._state.get('isCentralizedFilterEnabled');
    }

    setIsCentralizedFilterEnabled(isCentralizedFilterEnabled: boolean): this {
        return this.clone(
            this._state.set(
                'isCentralizedFilterEnabled',
                isCentralizedFilterEnabled
            )
        );
    }

    get isOneYearDateRangeEnabled(): boolean {
        return this._state.get('isOneYearDateRangeEnabled');
    }

    setIsOneYearDateRangeEnabled(isOneYearDateRangeEnabled: boolean): this {
        return this.clone(
            this._state.set(
                'isOneYearDateRangeEnabled',
                isOneYearDateRangeEnabled
            )
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class EmailDigestSettings extends ImmutableModel {
    static fromJS(json: EmailDigestSettingsModelType): EmailDigestSettings {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): EmailDigestSettingsModelType {
        return {
            isEnabled: this.isEnabled,
            subscriptionLimit: this.subscriptionLimit
        };
    }

    get isEnabled(): boolean {
        return this._state.get('isEnabled');
    }

    setIsEnabled(isEnabled: boolean): this {
        return this.clone(this._state.set('isEnabled', isEnabled));
    }

    get subscriptionLimit(): number {
        return this._state.get('subscriptionLimit');
    }

    setSubscriptionLimit(subscriptionLimit: number): this {
        return this.clone(
            this._state.set('subscriptionLimit', subscriptionLimit)
        );
    }
}
